/**
 * InsightBrowserFilters Component
 *
 * Filter controls for the Insight Browser:
 * - Freitext search (searches content, agent, project, type)
 * - Agent dropdown
 * - Project dropdown
 * - Type dropdown
 * - Embedding status dropdown
 * - Clear all filters button
 */

import { Search, X, Filter } from 'lucide-react';

export default function InsightBrowserFilters({
  filters,
  onFilterChange,
  agents = [],
  projects = [],
  insightTypes = [],
}) {
  const hasActiveFilters =
    filters.search ||
    filters.agentId ||
    filters.projectId ||
    filters.type ||
    filters.hasEmbedding !== null;

  const handleClearAll = () => {
    onFilterChange({
      search: '',
      agentId: null,
      projectId: null,
      type: null,
      hasEmbedding: null,
    });
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-300">Filter</span>
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="ml-auto text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-5 gap-3">
        {/* Freitext Search */}
        <div className="col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            placeholder="Search content, agent, project, type..."
            className="w-full pl-10 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Agent Dropdown */}
        <select
          value={filters.agentId || ''}
          onChange={(e) => onFilterChange({ ...filters, agentId: e.target.value || null })}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.displayName || agent.name}
            </option>
          ))}
        </select>

        {/* Project Dropdown */}
        <select
          value={filters.projectId || ''}
          onChange={(e) => onFilterChange({ ...filters, projectId: e.target.value || null })}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Projects</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.displayName || project.name}
            </option>
          ))}
        </select>

        {/* Type Dropdown */}
        <select
          value={filters.type || ''}
          onChange={(e) => onFilterChange({ ...filters, type: e.target.value || null })}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Types</option>
          {insightTypes.map((type) => (
            <option key={type.name} value={type.name}>
              {type.displayName || type.name}
            </option>
          ))}
        </select>
      </div>

      {/* Second Row: Embedding Status */}
      <div className="mt-3 flex items-center gap-3">
        <span className="text-xs text-gray-500">Embedding:</span>
        <select
          value={filters.hasEmbedding === null ? '' : filters.hasEmbedding ? 'true' : 'false'}
          onChange={(e) => {
            const val = e.target.value;
            onFilterChange({
              ...filters,
              hasEmbedding: val === '' ? null : val === 'true',
            });
          }}
          className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-md text-xs text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="">Any</option>
          <option value="true">Has Embedding</option>
          <option value="false">No Embedding</option>
        </select>
      </div>
    </div>
  );
}
